import {
	CheckCircleFilled,
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	EditOutlined,
	InboxOutlined,
	UploadOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import {
	Button,
	Col,
	Menu,
	Modal,
	Result,
	Row,
	Steps,
	Table,
	Tooltip,
	Upload,
	UploadFile,
} from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import { importTimesheetApi } from 'Api/importTimesheet';
import { Loader } from 'components/Global';
import Ellipse from 'components/Global/Ellipse';
import GlobalPayPeriod from 'components/Global/PayPeriod';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { DeleteActionSvg, DownloadSvg } from 'utils/svgs';
import { convertArrayToCSV, invalidText, toastText } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
const { Step } = Steps;

const steps = [
	{
		title: 'Upload CSV/Excel',
	},
	{
		title: 'Validation Results',
	},
	{
		title: 'Correct Data',
	},
	{
		title: 'Confirmation',
	},
];
const BulkTimeSheetImport = ({
	staffCode,
	fetchEmployeesTimeActivities,
}: {
	staffCode: string;
	fetchEmployeesTimeActivities: () => void;
}) => {
	const [isModalVisible, setIsModalVisible] = useState(false);

	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [isUploadLoading, setIsUploadLoading] = useState(false);

	const [validationResults, setValidationResults] = useState([]);

	const [finalData, setFinalData] = useState([]);

	const [isErrorModal, setIsErrorModal] = useState(false);
	const [errorData, setErrorData] = useState<any>(null);

	const [currentStep, setCurrentStep] = useState(0);
	const [isImportLoading, setIsImportLoading] = useState(false);

	const [selectedYear, setSelectedYear] = useState<any>(dayjs());
	const [selectedPayPeriod, setSelectedPayPeriod] = useState('');

	const [payPeriodError, setPayPeriodError] = useState(false);
	const [NrcUpdateObject, setNrcUpdateObject] = useState<any>(null);

	const handlePayPeriod = (value: string) => {
		if (invalidText(value)) {
			setPayPeriodError(true);
			return;
		}

		setSelectedPayPeriod(value);
	};

	const handleYearChange = (value: any) => {
		setSelectedYear(value);

		setSelectedPayPeriod('');
	};

	const handleModalClose = () => {
		setIsModalVisible(false);
		setErrorData(false);
		setFileList([]);
		setCurrentStep(0);
		setFinalData([]);
		setErrorData([]);
		setIsErrorModal(false);
		setIsImportLoading(false);
		setIsUploadLoading(false);
	};

	const handleImportMore = () => {
		setErrorData(false);
		setFileList([]);
		setCurrentStep(0);
		setFinalData([]);
		setErrorData([]);
		setIsErrorModal(false);
		setIsImportLoading(false);
		setIsUploadLoading(false);
	};

	const isRecordUpdatable = (record: any) => {
		if (!NrcUpdateObject || !record['National Registration Number'])
			return false;

		// Find the corresponding entry in NrcUpdateObject
		const matchingEntry = NrcUpdateObject.find(
			(item: any) =>
				Object.keys(item)[0] === record['National Registration Number']
		);

		return (
			matchingEntry &&
			matchingEntry[record['National Registration Number']] === true
		);
	};

	const next = async () => {
		if (invalidText(selectedPayPeriod)) {
			toastText('Please select a pay period', 'error');
			return;
		}
		if (currentStep === 1) {
			const data = validationResults.filter((item: any) => item._isValid);
			setFinalData(data);
		}
		if (currentStep === 2) {
			await importSalaryDetails(finalData);
		} else {
			setCurrentStep((prev: any) => {
				return Math.min(prev + 1, steps.length - 1);
			});
		}
	};

	const prev = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 0));
		if (currentStep === 1) {
			setFileList([]);
		}
	};

	const getClassName = (isValid: boolean) => {
		if (!isValid) {
			return 'required-color';
		} else {
			return '';
		}
	};

	const handleDelete = useCallback(
		(index: number) => {
			// Use slicing for immutable updates (more efficient than filter for large arrays)
			const updatedResults = [
				...validationResults.slice(0, index),
				...validationResults.slice(index + 1),
			];
			setValidationResults(updatedResults);

			if (currentStep === 2) {
				const updatedFinalData = [
					...finalData.slice(0, index),
					...finalData.slice(index + 1),
				];
				setFinalData(updatedFinalData);
			}

			toastText('Record deleted Successfully', 'success');
		},
		[validationResults, finalData, currentStep]
	);

	const columns: any = useMemo(() => {
		if (!validationResults.length) return [];

		return [
			{
				title: 'Status',
				key: '_validationStatus',
				className: 'bg-white',
				width: 80,
				render: (_: any, record: any) =>
					record._isValid ? (
						<div
							className="text-green-500 bg-table"
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'green',
							}}
						>
							<CheckCircleOutlined />
						</div>
					) : (
						<div
							className="text-red-500 bg-table"
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'red',
							}}
						>
							<CloseCircleOutlined />
						</div>
					),
			},
			{
				title: 'Validation',
				key: '_errors',
				className: 'bg-white',
				width: 105,
				render: (_: any, record: any) => {
					return !record._isValid ? (
						<p
							onClick={() => {
								setIsErrorModal(true);
								setErrorData(record);
							}}
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'orange',
							}}
							className={`pointer ${getClassName(
								record._isValid
							)}`}
						>
							<WarningOutlined />
						</p>
					) : null;
				},
			},
			{
				title: 'Action',
				key: 'action',
				className: 'bg-white',
				width: 80,
				fixed: 'left',
				render: (_: any, record: any, index: number) => {
					return (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
							}}
						>
							<Tooltip title="Delete">
								<p
									className="cursor-pointer"
									onClick={() => {
										handleDelete(index);
									}}
								>
									<DeleteActionSvg />
								</p>
							</Tooltip>
						</div>
					);
				},
			},
			...Object.keys(validationResults[0] || {})
				.filter((key) => !key.startsWith('_'))
				.map((key) => ({
					title: key,
					dataIndex: key,
					key: key,
					width: 150,
					ellipsis: true,
					className: 'bg-table bg-white',
					...(key === 'Employee Id' && {
						fixed: 'left',
						width: 180,
					}),
					render: (text: string, record: any) => {
						// Check if this is the First Name column and if the NRC is marked for update
						const isFirstNameWithEdit =
							key === 'Employee Id' && isRecordUpdatable(record);

						return (
							<div
								style={{
									minWidth: 150,
									display: 'flex',
									alignItems: 'center',
								}}
								className={`${getClassName(record._isValid)}`}
							>
								<Ellipse
									maxLength={20}
									message={text}
									isTooltip={true}
									tooltipMessage={text}
									key={text}
								/>

								{isFirstNameWithEdit && (
									<Tooltip title="Existing timesheet record found ,it will be updated with the latest details">
										<EditOutlined
											style={{
												marginLeft: 10,
												color: '#584495',
											}}
										/>
									</Tooltip>
								)}
							</div>
						);
					},
				})),
		];
	}, [handleDelete, validationResults]);

	const downloadCsv = async () => {
		try {
			const response =
				await importTimesheetApi.downloadTimeSheetCsvTemplate(
					staffCode
				);
			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'text/csv',
				})
			);

			const link = document.createElement('a');
			link.href = url;
			link.setAttribute(
				'download',
				`${staffCode.toLowerCase()}_timesheet.csv`
			);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (err) {
			toastText(
				'Something went wrong in downloading csv template',
				'error'
			);
		}
	};

	const handleUpload = async (file: any) => {
		try {
			setIsUploadLoading(true);

			// Detect file type
			const fileExtension = file.name
				.substring(file.name.lastIndexOf('.'))
				.toLowerCase();
			const mimeType = file.type;

			const isExcel =
				fileExtension === '.xlsx' ||
				fileExtension === '.xls' ||
				mimeType ===
					'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
				mimeType === 'application/vnd.ms-excel';

			if (isExcel) {
				// For Excel: Skip frontend validation, let backend handle it
				let formData = new FormData();
				formData.append('documentName', 'timesheet_csv');
				formData.append('file', file);
				formData.append('staffCode', staffCode);

				const validatedData: any =
					await importTimesheetApi.uploadTimeSheetCsv(
						formData,
						selectedPayPeriod
					);

				const apiResponseData = validatedData.data.data;

				const mappedNrcData = apiResponseData.map(
					(item: Record<string, any>) => {
						return {
							[item['National Registration Number']]:
								item.isUpdated,
						};
					}
				);

				setNrcUpdateObject(mappedNrcData);

				const cleanedData = validatedData.data.data.map(
					(item: Record<string, any>) => {
						return Object.fromEntries(
							Object.entries(item).filter(([key]) => {
								return key !== 'isUpdated';
							})
						);
					}
				);

				setValidationResults(cleanedData);
				setIsModalVisible(true);
				setCurrentStep(1);
			} else {
				// For CSV: Keep existing frontend header validation
				const reader = new FileReader();
				reader.onload = async (e: any) => {
					try {
						const csvText = e.target.result;
						const [headerLine] = csvText.split('\n');
						const uploadedColumns = headerLine
							.split(',')
							.map((col: any) =>
								col.trim().replace(/^"(.*)"$/, '$1')
							);

						// ✅ Define expected columns based on staff code
						let jsonData: Record<string, string>;

						// Common fields for all staff types
						const commonFields = {
							'Employee Id': 'EMP001',
							'Employee Name': 'John Doe',
							'National Registration Number*': '123456/12/1',
						};

						const timeFields = {
							'WeekDay Overtime Hours*': '8',
							'Double Overtime Hours*': '0',
							'Shift Difference Hours*': '0',
							'Leave Without Pay Leave*': '0',
						};

						// Build expected columns based on staff code
						if (staffCode === 'HOURLY') {
							jsonData = {
								...commonFields,
								'Regular Hours*': '40',
								...timeFields,
							};
						} else if (staffCode === 'MONTHLY') {
							jsonData = {
								...commonFields,
								...timeFields,
							};
						} else {
							toastText('Invalid staff code selected', 'error');
							setIsUploadLoading(false);
							setFileList([]);
							return;
						}

						const requiredColumns = Object.keys(jsonData);

						// Check for missing columns
						const missingColumns = requiredColumns.filter(
							(col) => !uploadedColumns.includes(col)
						);

						// Check for extra columns (columns that shouldn't be there)
						const extraColumns = uploadedColumns.filter(
							(col: string) =>
								!requiredColumns.includes(col) &&
								col.trim() !== ''
						);

						if (
							missingColumns.length > 0 ||
							extraColumns.length > 0
						) {
							let message = `Please download the correct ${staffCode} CSV/Excel template:\n\n`;

							if (missingColumns.length > 0) {
								const displayMissing = missingColumns.slice(
									0,
									3
								);
								message += `Missing columns: ${displayMissing.join(
									', '
								)}`;
								if (missingColumns.length > 3) {
									message += ` and ${
										missingColumns.length - 3
									} more`;
								}
								message += '\n';
							}

							if (extraColumns.length > 0) {
								const displayExtra = extraColumns.slice(0, 3);
								message += `Unexpected columns: ${displayExtra.join(
									', '
								)}`;
								if (extraColumns.length > 3) {
									message += ` and ${
										extraColumns.length - 3
									} more`;
								}
							}

							toastText(message, 'error');
							setIsUploadLoading(false);
							setFileList([]);
							setCurrentStep(0);
							return;
						}

						// If validation passes, proceed with upload
						let formData = new FormData();
						formData.append('documentName', 'timesheet_csv');
						formData.append('file', file);
						formData.append('staffCode', staffCode);
						const validatedData: any =
							await importTimesheetApi.uploadTimeSheetCsv(
								formData,
								selectedPayPeriod
							);

						const apiResponseData = validatedData.data.data;

						const mappedNrcData = apiResponseData.map(
							(item: Record<string, any>) => {
								return {
									[item['National Registration Number']]:
										item.isUpdated,
								};
							}
						);

						setNrcUpdateObject(mappedNrcData);

						const cleanedData = validatedData.data.data.map(
							(item: Record<string, any>) => {
								return Object.fromEntries(
									Object.entries(item).filter(([key]) => {
										return key !== 'isUpdated';
									})
								);
							}
						);

						setValidationResults(cleanedData);
						setIsModalVisible(true);
						setCurrentStep(1);
					} catch (error: any) {
						let message =
							error?.response?.data?.message ||
							`Failed to process file. Please try again`;
						toastText(message, 'error');
					} finally {
						setIsUploadLoading(false);
					}
				};

				reader.onerror = () => {
					toastText('File reading failed', 'error');
					setIsUploadLoading(false);
				};

				reader.readAsText(file);
				return false;
			}
		} catch (error: any) {
			let message =
				error?.response?.data?.message ||
				`Failed to process file. Please try again`;
			toastText(message, 'error');
			setIsUploadLoading(false);
		} finally {
			setIsUploadLoading(false);
		}

		return false;
	};
	const importSalaryDetails = async (data: any) => {
		try {
			setIsImportLoading(true);

			const csvString = convertArrayToCSV(data);

			const blob = new Blob([csvString], { type: 'text/csv' });

			const formData = new FormData();
			formData.append('file', blob, 'data.csv');

			formData.append('staffCode', staffCode);
			const response =
				await importTimesheetApi.validateDataFromAPIForTimeSheet(
					formData,
					selectedPayPeriod
				);
			toastText(response.data.message, 'success');
			setCurrentStep(3);
			await fetchEmployeesTimeActivities();
		} catch (error: any) {
			let errorMessage =
				error?.response?.data?.message ||
				'Something went wrong please while importing time sheet details';
			toastText(errorMessage, 'error');
		} finally {
			setIsImportLoading(false);
		}
	};

	return (
		<>
			<Tooltip title="Time sheet Action">
				<p style={{ padding: '5px', color: '#584495' }}>
					<span style={{ fontSize: '17px', marginTop: '12px' }}>
						<ClockCircleOutlined />{' '}
					</span>
					Time sheet Action
				</p>
				<Menu.Item
					style={{ backgroundColor: 'white' }}
					key="4"
					onClick={() => setIsModalVisible(true)}
					icon={<UploadOutlined />}
				>
					Upload Time sheet CSV/Excel File
				</Menu.Item>
			</Tooltip>

			<Modal
				open={isModalVisible}
				onCancel={handleModalClose}
				width="1600px"
				footer={null}
				rootClassName="uploadCsvModal"
				destroyOnClose
				maskClosable={false}
				closable={false}
				style={{
					maxHeight: 'calc(100vh - 200px)', // Fixed height for the modal
					display: 'flex',
					flexDirection: 'column',
					// overflowY: 'scroll',
				}}
			>
				<div>
					<Steps current={currentStep}>
						{steps.map((step, index) => (
							<Step key={step.title} title={step.title} />
						))}
					</Steps>

					<div
						style={{
							marginTop: '20px',
							marginBottom: '0px 20px 20px 20px',
							padding: '20px',
							border: '1px solid #d9d9d9',
							borderRadius: '5px',
							flex: 1,
							overflow: 'hidden',
							display: 'flex',
							flexDirection: 'column',
							minHeight: '200px',
							justifyContent: 'center',
						}}
					>
						{currentStep === 0 && (
							<div className={styles['document-upload']}>
								{isUploadLoading ? (
									<Loader />
								) : (
									<>
										<Row>
											<Col
												span={24}
												style={{
													paddingTop: '0px',
													display: 'flex',
													justifyContent: 'end',
												}}
											>
												<Tooltip title="Download Timesheet CSV/Excel Template">
													<p
														className={
															styles[
																'template-link'
															]
														}
														onClick={downloadCsv}
													>
														<div
															style={{
																justifyContent:
																	'center',
																display: 'flex',
															}}
														>
															<DownloadSvg />
														</div>
														Download Template
													</p>
												</Tooltip>
											</Col>
										</Row>
										<Row>
											<Col
												span={12}
												style={{
													paddingBottom: '10px',
													paddingTop: '0px',
												}}
											>
												<GlobalPayPeriod
													selectedYear={selectedYear}
													handleSelectedYear={(
														value
													) => {
														handleYearChange(value);
													}}
													selectedPayPeriod={
														selectedPayPeriod
													}
													handlePayPeriod={(value) =>
														handlePayPeriod(value)
													}
													rowStyle={{
														marginTop: '10px',
														width: '100%',
														gap: '16px',
													}}
													payPeriodError={
														payPeriodError
													}
													isPayPeriodRequired
													isRow={false}
												/>
											</Col>
										</Row>
										<Dragger
											name="file"
											multiple={false}
											accept=".csv,.xlsx,.xls"
											fileList={fileList}
											beforeUpload={(file) => {
												const fileExtension = file.name
													.substring(
														file.name.lastIndexOf(
															'.'
														)
													)
													.toLowerCase();
												const mimeType = file.type;

												const isCSVOrExcel =
													fileExtension === '.csv' ||
													fileExtension === '.xlsx' ||
													fileExtension === '.xls' ||
													mimeType === 'text/csv' ||
													mimeType ===
														'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
													mimeType ===
														'application/vnd.ms-excel';

												if (!isCSVOrExcel) {
													toastText(
														'Invalid file format. Please upload CSV or Excel file',
														'error'
													);
													return Upload.LIST_IGNORE;
												}
												if (
													invalidText(
														selectedPayPeriod
													)
												) {
													toastText(
														'Please select a pay period',
														'error'
													);
													return Upload.LIST_IGNORE;
												}
												handleUpload(file);
												return false;
											}}
											onChange={(info) => {
												let newFileList = [
													...info.fileList,
												];

												// Limit to one file
												newFileList =
													newFileList.slice(-1);

												setFileList(newFileList);
											}}
										>
											<p className="ant-upload-drag-icon">
												<InboxOutlined />
											</p>
											<p className="ant-upload-text">
												Click or drag CSV/Excel file to
												this area to upload
											</p>
											<p className="ant-upload-hint">
												Support for a single CSV or
												Excel file upload (up to{' '}
												<span
													style={{ color: '#584495' }}
												>
													200 records
												</span>
												)
											</p>
										</Dragger>
									</>
								)}
							</div>
						)}

						{currentStep === 1 && (
							<div className="validation-results">
								<Table
									columns={columns}
									dataSource={validationResults ?? []}
									scroll={{
										x: 'max-content',
										y: 'calc(100vh - 400px)',
									}}
									pagination={false}
									size="middle"
									bordered
									tableLayout="fixed"
									footer={() => (
										<div style={{ textAlign: 'right' }}>
											Total No. of Employees :{' '}
											{validationResults?.length || 0}
										</div>
									)}
								/>
							</div>
						)}

						{currentStep === 2 && (
							<div className="validation-results">
								<Table
									columns={columns.slice(0, -2)}
									dataSource={finalData ?? []}
									scroll={{
										x: 'max-content',
										y: 'calc(100vh - 400px)',
									}}
									pagination={false}
									loading={isImportLoading}
									size="middle"
									bordered
									tableLayout="fixed"
									footer={() => (
										<div style={{ textAlign: 'right' }}>
											Total No. of Employees :{' '}
											{finalData?.length || 0}
										</div>
									)}
								/>
							</div>
						)}

						{currentStep === 3 && (
							<Result
								status="success"
								title="The process of importing Time sheet has started."
								subTitle={`Importing ${finalData.length} time sheet record. Only ${finalData.length} is validated and imported. This may take a moment, Please wait....`}
								icon={
									<CheckCircleFilled
										style={{ color: '#584495' }}
									/>
								}
								extra={[
									<Button
										type="primary"
										key="console"
										onClick={handleImportMore}
									>
										Import More
									</Button>,
									<Button
										key="buy"
										onClick={handleModalClose}
									>
										Close
									</Button>,
								]}
							/>
						)}
					</div>

					{currentStep !== 3 && (
						<div
							style={{
								marginTop: '20px',
								display: 'flex',
								justifyContent: 'end',
								gap: '15px',
							}}
						>
							<Button onClick={handleModalClose}>Cancel</Button>
							{currentStep !== 0 && (
								<Button
									disabled={
										currentStep === 0 ||
										isImportLoading ||
										isUploadLoading
									}
									onClick={prev}
									type="primary"
								>
									Previous
								</Button>
							)}
							{fileList.length > 0 && (
								<Button
									disabled={
										currentStep === steps.length - 1 ||
										isImportLoading ||
										isUploadLoading ||
										(currentStep === 1 &&
											validationResults.length === 0) ||
										(currentStep === 2 &&
											finalData.length === 0)
									}
									type="primary"
									onClick={next}
								>
									{currentStep === 2
										? 'Import Timesheet Details'
										: 'Next'}
								</Button>
							)}
						</div>
					)}
				</div>
			</Modal>

			{errorData && isErrorModal && (
				<Modal
					open={isErrorModal}
					onCancel={() => {
						setErrorData(null);
						setIsErrorModal(false);
					}}
					destroyOnClose
					rootClassName="uploadCsvModal"
					width="90%"
					footer={null}
				>
					<ul
						className="error-list"
						style={{
							listStyleType: 'none',
							padding: '10px',
							borderRadius: '5px',
							border: '1px solid #d4d4d4',
							color: '#721c24',
							fontFamily: 'Arial, sans-serif',
							maxHeight: '450px',
							overflowY: 'auto',
						}}
					>
						{Object.entries(errorData._validationErrors).map(
							([field, error]: any) => (
								<li
									key={field}
									style={{
										padding: '8px 12px',
										borderBottom: '1px solid #d4d4d4',
									}}
								>
									<span
										style={{
											fontWeight: 'bold',
											color: '#b02a2a',
										}}
									>
										{field}
									</span>{' '}
									:{' '}
									<span
										style={{
											fontStyle: 'italic',
											color: 'black',
										}}
									>
										{error}
									</span>
								</li>
							)
						)}
					</ul>
				</Modal>
			)}
		</>
	);
};

export default BulkTimeSheetImport;
